import type { ChangeEvent, FocusEvent } from 'react';
import { useState } from 'react';

import { cn } from '../cn';
import Input from './Input';
import type { InputProps } from './Input';

export type ColorInputProps = Omit<InputProps, 'icon' | 'type' | 'value' | 'defaultValue' | 'onChange'> & {
  /** Canonical hex color, e.g. `'#3b82f6'`. */
  value: string;
  /** Fired with a normalized `#rrggbb` whenever the field resolves to a valid hex. */
  onChange: (hex: string) => void;
};

/** Normalize freeform hex input (with or without `#`, 3- or 6-digit) to `#rrggbb`, or `null` if invalid. */
function toHex6(input: string): string | null {
  const raw = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    const [r, g, b] = raw.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
}

/**
 * Hex text input with a clickable color swatch in the left-icon slot. Clicking the
 * swatch opens the browser's native color picker; picking there updates the hex text,
 * and editing the hex text updates the swatch — two-way bound. Inherits the full
 * `Input` `size` / `color` / `tone` / `error` / `disabled` / `readOnly` API.
 */
export default function ColorInput({
  value,
  onChange,
  onBlur,
  disabled,
  readOnly,
  placeholder = '#000000',
  ...rest
}: ColorInputProps) {
  // Local draft lets the field hold a partial/invalid hex while typing; we only
  // commit a normalized `#rrggbb` upstream. Sync from `value` during render
  // (no useEffect) when the parent changes it — but skip when the incoming value
  // is just an echo of our own normalized draft, so a controlled parent feeding
  // our `onChange` value back in doesn't make the field "jump" mid-typing.
  const [draft, setDraft] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    if (toHex6(draft) !== value) setDraft(value);
  }

  const swatchHex = toHex6(draft) ?? toHex6(value) ?? '#000000';
  const locked = disabled === true || readOnly === true;

  const onText = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
    const hex = toHex6(e.target.value);
    if (hex) onChange(hex);
  };

  // Native color input always emits a valid `#rrggbb`.
  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
    onChange(e.target.value);
  };

  // Re-canonicalize a valid draft, or revert an invalid one to the last good value.
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setDraft(toHex6(draft) ?? value);
    onBlur?.(e);
  };

  const swatch = (
    <span
      className="relative block overflow-hidden rounded-full ring-1 ring-black/10 dark:ring-white/20"
      style={{ backgroundColor: swatchHex }}>
      <input
        type="color"
        value={swatchHex}
        onChange={onPick}
        disabled={locked}
        aria-label="Pick a color"
        className={cn(
          'absolute inset-0 size-full cursor-pointer opacity-0',
          locked && 'pointer-events-none cursor-default',
        )}
      />
    </span>
  );

  return (
    <Input
      {...rest}
      type="text"
      icon={swatch}
      value={draft}
      onChange={onText}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
      spellCheck={false}
      autoComplete="off"
    />
  );
}
